'use client'

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { createPortal } from 'react-dom'
import Image from 'next/image'
import { getStrapiMedia } from '@/lib/strapi/client'
import type { TeaserImageGridItemType } from '@/lib/types/teaser'
import { useHorizontalDragScroll } from '@/hooks/useHorizontalDragScroll'

type Props = {
  items: TeaserImageGridItemType[]
  activeIndex: number
  onClose: () => void
  onChangeIndex: (index: number) => void
}

const MIN_ZOOM = 1
const MAX_ZOOM = 2.5
const ZOOM_STEP = 0.25

function subscribe() {
  return () => {}
}

function imageCaption(item: TeaserImageGridItemType | undefined): string {
  const caption = item?.image?.caption?.trim()
  if (caption) return caption
  const alt = item?.image?.alternativeText?.trim()
  if (alt) return alt
  return ''
}

function imageAlt(
  item: TeaserImageGridItemType | undefined,
  index: number,
): string {
  const alt = item?.image?.alternativeText?.trim()
  if (alt) return alt
  const caption = item?.image?.caption?.trim()
  if (caption) return caption
  return `Gallery image ${index + 1}`
}

export default function TeaserImageGridLightbox({
  items,
  activeIndex,
  onClose,
  onChangeIndex,
}: Props) {
  const titleId = useId()
  const mounted = useSyncExternalStore(subscribe, () => true, () => false)
  const [zoom, setZoom] = useState(MIN_ZOOM)
  const activeThumbRef = useRef<HTMLButtonElement>(null)
  const imageSwipeRef = useRef<{
    pointerId: number
    startX: number
    startY: number
  } | null>(null)
  const { scrollerRef: thumbStripRef, dragging: thumbDragging, dragHandlers: thumbDragHandlers } =
    useHorizontalDragScroll()

  const total = items.length
  const active = items[activeIndex]
  const activeSrc = active?.image?.url ? getStrapiMedia(active.image.url) : null
  const activeCaption = imageCaption(active)
  const activeAlt = imageAlt(active, activeIndex)

  const selectIndex = useCallback(
    (index: number) => {
      setZoom(MIN_ZOOM)
      onChangeIndex(index)
    },
    [onChangeIndex],
  )

  const goPrev = useCallback(() => {
    if (total < 2) return
    selectIndex((activeIndex - 1 + total) % total)
  }, [activeIndex, selectIndex, total])

  const goNext = useCallback(() => {
    if (total < 2) return
    selectIndex((activeIndex + 1) % total)
  }, [activeIndex, selectIndex, total])

  const onImagePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (total < 2 || zoom > MIN_ZOOM || event.button !== 0) return
      imageSwipeRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
      }
      event.currentTarget.setPointerCapture(event.pointerId)
    },
    [total, zoom],
  )

  const onImagePointerUp = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const swipe = imageSwipeRef.current
      if (!swipe || event.pointerId !== swipe.pointerId) return
      imageSwipeRef.current = null
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }

      const dx = event.clientX - swipe.startX
      const dy = event.clientY - swipe.startY
      // Horizontal swipe only (ignore mostly-vertical gestures).
      if (Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy)) return
      if (dx > 0) goPrev()
      else goNext()
    },
    [goNext, goPrev],
  )

  const onImagePointerCancel = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      imageSwipeRef.current = null
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }
    },
    [],
  )

  useEffect(() => {
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
      if (event.key === 'ArrowLeft') goPrev()
      if (event.key === 'ArrowRight') goNext()
    }

    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = prevOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [goNext, goPrev, onClose])

  // Keep the selected thumbnail fully visible (avoid clipped ends from centered overflow).
  useEffect(() => {
    activeThumbRef.current?.scrollIntoView({
      behavior: 'smooth',
      inline: 'nearest',
      block: 'nearest',
    })
  }, [activeIndex])

  if (!mounted || !activeSrc) return null

  const zoomOut = () => setZoom((z) => Math.max(MIN_ZOOM, Number((z - ZOOM_STEP).toFixed(2))))
  const zoomIn = () => setZoom((z) => Math.min(MAX_ZOOM, Number((z + ZOOM_STEP).toFixed(2))))
  const resetZoom = () => setZoom(MIN_ZOOM)

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-labelledby={activeCaption ? titleId : undefined}
      aria-label={activeCaption ? undefined : activeAlt}
      data-fig="teaser-image-grid-lightbox"
    >
      <button
        type="button"
        aria-label="Close gallery backdrop"
        className="absolute inset-0 bg-[rgba(1,10,14,0.9)] backdrop-blur-[8px]"
        onClick={onClose}
      />

      <div className="relative z-10 flex h-full flex-col">
        <header className="flex shrink-0 items-start justify-between gap-4 px-5 py-4 md:px-8 md:py-5">
          <p
            id={titleId}
            className="min-w-0 flex-1 whitespace-normal break-words font-body text-base font-medium leading-[23px] text-white md:text-lg md:leading-6"
          >
            {activeCaption || activeAlt}
          </p>
          <button
            type="button"
            aria-label="Close gallery"
            onClick={onClose}
            className="flex size-8 shrink-0 cursor-pointer items-center justify-center text-white transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path
                d="M5 5L15 15M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        <div className="relative flex min-h-0 flex-1 items-center justify-center px-16 md:px-20 lg:px-24">
          {total > 1 && (
            <button
              type="button"
              aria-label="Previous image"
              onClick={goPrev}
              className="absolute left-2 z-20 flex size-[36px] cursor-pointer items-center justify-center rounded-full bg-[#5F5A50] text-white transition-colors hover:bg-surface-white hover:text-[#000000] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white md:left-6"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M14.5 6.5L9 12L14.5 17.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}

          <div
            className={`relative h-full w-full max-w-[1070px] overflow-hidden rounded-[12px] touch-pan-y md:rounded-[20px] ${
              total > 1 && zoom <= MIN_ZOOM ? 'cursor-grab active:cursor-grabbing' : ''
            }`}
            onPointerDown={onImagePointerDown}
            onPointerUp={onImagePointerUp}
            onPointerCancel={onImagePointerCancel}
          >
            <div
              className="absolute inset-0 transition-transform duration-200 ease-out"
              style={{ transform: `scale(${zoom})` }}
            >
              <Image
                src={activeSrc}
                alt={activeAlt}
                fill
                priority
                draggable={false}
                className="pointer-events-none select-none object-contain md:object-cover"
                sizes="(max-width: 991px) 100vw, 1070px"
              />
            </div>
          </div>

          {total > 1 && (
            <button
              type="button"
              aria-label="Next image"
              onClick={goNext}
              className="absolute right-2 z-20 flex size-[36px] cursor-pointer items-center justify-center rounded-full bg-[#5F5A50] text-white transition-colors hover:bg-surface-white hover:text-[#000000] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white md:right-6"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M9.5 6.5L15 12L9.5 17.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>

        <div className="relative z-10 flex shrink-0 flex-col items-center gap-4 px-4 pb-6 pt-3 md:gap-5 md:pb-8 md:pt-4">
          {total > 1 && (
            <div
              ref={thumbStripRef}
              className={`w-full max-w-full touch-pan-x overflow-x-auto overscroll-x-contain px-1 py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${
                thumbDragging ? 'cursor-grabbing select-none' : 'cursor-grab'
              }`}
              tabIndex={0}
              role="group"
              aria-label="Gallery thumbnails"
              {...thumbDragHandlers}
            >
              <div className="mx-auto flex w-max min-w-full items-center justify-center gap-3 px-3 md:gap-4 md:px-4">
                {items.map((item, index) => {
                  const src = item.image?.url ? getStrapiMedia(item.image.url) : null
                  if (!src) return null
                  const selected = index === activeIndex
                  return (
                    <button
                      key={item.id}
                      ref={selected ? activeThumbRef : undefined}
                      type="button"
                      aria-label={imageAlt(item, index)}
                      aria-current={selected ? 'true' : undefined}
                      onClick={() => selectIndex(index)}
                      className={`relative h-12 w-[55px] shrink-0 overflow-hidden rounded-lg transition-shadow md:h-14 md:w-[90px] ${
                        selected
                          ? 'border-[1.5px] border-brand-primary shadow-[0px_0px_8px_rgba(255,255,255,0.42)]'
                          : 'border border-transparent opacity-90 hover:opacity-100'
                      }`}
                    >
                      <Image
                        src={src}
                        alt=""
                        fill
                        draggable={false}
                        className="pointer-events-none object-cover"
                        sizes="90px"
                      />
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          <div className="flex h-[38px] items-center gap-3 rounded-[40px] bg-[#404040] px-2.5 md:gap-3">
            <button
              type="button"
              aria-label="Zoom out"
              onClick={zoomOut}
              disabled={zoom <= MIN_ZOOM}
              className="flex size-6 cursor-pointer items-center justify-center text-white/50 transition-colors hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 12H19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
            <button
              type="button"
              aria-label="Reset zoom"
              onClick={resetZoom}
              className="flex size-7 cursor-pointer items-center justify-center text-white transition-opacity hover:opacity-80"
            >
              <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="7" stroke="currentColor" strokeWidth="1.5" />
                <path
                  d="M17.5 17.5L23 23"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            <button
              type="button"
              aria-label="Zoom in"
              onClick={zoomIn}
              disabled={zoom >= MAX_ZOOM}
              className="flex size-6 cursor-pointer items-center justify-center text-white transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 5V19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path d="M5 12H19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
